/**
 * Sellograph Tracking — Nuxt 3/4 Client Plugin
 *
 * Was dieses Plugin tut:
 * 1. Laedt sf.js SELBST von sellographWorkerUrl und initialisiert es mit dem
 *    Token — KEIN separater app.head.script-Eintrag in nuxt.config.ts noetig.
 *    (Frueher musste sf.js manuell via nuxt.config.ts geladen werden; das
 *    fehlende Head-Script war die haeufigste Fehlkonfiguration — der Shop
 *    sendete dann still ueberhaupt keine Events.)
 * 2. Faengt UTM-Parameter und Click-IDs aus der URL
 * 3. Injiziert UTM-Daten UND die Visitor-/Session-Identitaet automatisch als
 *    X-Sellograph-* Headers in alle Backend-API Requests (kein manueller Code
 *    noetig). Die Identitaets-Header sind fuer Headless-Setups zwingend: das
 *    _sg_vid-Cookie liegt auf der Storefront-Domain und erreicht ein Backend
 *    auf anderer (Sub-)Domain nicht.
 *    Injiziert wird auf ZWEI Ebenen: globalThis.$fetch (Nuxt) UND window.fetch
 *    (nativ) — letzteres ist zwingend, weil @shopware/api-client & aehnliche
 *    Clients eine eigene ofetch-Instanz nutzen, die $fetch komplett umgeht.
 *
 * Funktioniert mit jedem Backend: Shopware 6, Magento 2, Custom APIs.
 *
 * Installation:
 *   1. Diese Datei als plugins/sellograph.client.ts in dein Nuxt-Projekt kopieren
 *   2. composables/useSellograph.ts kopieren
 *   3. nuxt.config.ts konfigurieren:
 *
 *      runtimeConfig: {
 *        public: {
 *          sellographToken: 'dein-snippet-token',
 *          sellographWorkerUrl: 'https://analytics.maximizly.io',  // oder deine First-Party-Domain
 *          backendApiUrl: 'https://dein-shop.de',  // Shopware, Magento, oder Custom
 *          sellographDebug: false,  // true = Debug-Modus aktivieren
 *        }
 *      }
 */

import { defineNuxtPlugin } from '#app';

const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;
const CLICK_IDS = ['gclid', 'fbclid', 'ttclid', 'msclkid'] as const;
const STORAGE_KEY = 'sellograph_utm';
const FIRST_TOUCH_KEY = 'sellograph_first_utm';

export interface SellographUtmData {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  click_id?: string;
  click_id_type?: string;
  landing_url?: string;
  referrer?: string;
}

// ─── Debug Logger ───────────────────────────────────────────────────

let _debug = false;

function dbg(label: string, ...args: any[]) {
  if (!_debug) return;
  console.log(
    `%c[Sellograph]%c ${label}`,
    'background:#6366f1;color:#fff;padding:1px 6px;border-radius:3px;font-weight:bold',
    'color:#6366f1;font-weight:bold',
    ...args,
  );
}

/**
 * Aktiviert/deaktiviert den Debug-Modus zur Laufzeit.
 * Kann auch in der Browser-Konsole aufgerufen werden:
 *   window.__sellographDebug(true)
 */
export function setSellographDebug(enabled: boolean) {
  _debug = enabled;
  if (typeof window !== 'undefined') {
    (window as any).__sg_debug = enabled;
  }
  if (enabled) {
    dbg('Debug-Modus aktiviert');
    dbg('Gespeicherte UTMs', getSellographUtmData());
    dbg('Aktuelle Headers', getSellographHeaders());
  }
}

// ─── UTM Capture ────────────────────────────────────────────────────

function captureUtmParams(): void {
  if (typeof window === 'undefined') return;

  const params = new URLSearchParams(window.location.search);
  const utmData: SellographUtmData = {};
  let hasUtm = false;

  for (const param of UTM_PARAMS) {
    const value = params.get(param);
    if (value) {
      utmData[param] = value;
      hasUtm = true;
    }
  }

  for (const id of CLICK_IDS) {
    const value = params.get(id);
    if (value) {
      utmData.click_id = value;
      utmData.click_id_type = id;
      hasUtm = true;
      break;
    }
  }

  if (!hasUtm) {
    dbg('Keine UTM-Parameter in URL');
    return;
  }

  utmData.landing_url = window.location.href;

  if (document.referrer && !document.referrer.includes(window.location.hostname)) {
    utmData.referrer = document.referrer;
  }

  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(utmData));
  dbg('UTM erfasst', utmData);

  if (!sessionStorage.getItem(FIRST_TOUCH_KEY)) {
    sessionStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(utmData));
    dbg('First-Touch gespeichert', utmData);
  }
}

// ─── Public API ─────────────────────────────────────────────────────

export function getSellographUtmData(): SellographUtmData {
  if (typeof window === 'undefined') return {};
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

/**
 * Visitor-/Session-Identitaet aus den sf.js-Speicherorten lesen.
 * sf.js persistiert die visitor_id in localStorage + 1P-Cookie `_sg_vid`
 * und die session_id in sessionStorage `_sg_sid`.
 */
export function getSellographIdentity(): { visitor_id?: string; session_id?: string } {
  if (typeof window === 'undefined') return {};
  const identity: { visitor_id?: string; session_id?: string } = {};
  try {
    const vid = localStorage.getItem('_sg_vid')
      || (document.cookie.match(/(?:^|;\s*)_sg_vid=([^;]+)/)?.[1] ?? '');
    if (vid) identity.visitor_id = vid;
    const sid = sessionStorage.getItem('_sg_sid') || '';
    if (sid) identity.session_id = sid;
  } catch { /* storage blocked — headers bleiben leer */ }
  return identity;
}

export function getSellographHeaders(): Record<string, string> {
  const utm = getSellographUtmData();
  const identity = getSellographIdentity();
  const headers: Record<string, string> = {};

  if (utm.utm_source) headers['X-Sellograph-Utm-Source'] = utm.utm_source;
  if (utm.utm_medium) headers['X-Sellograph-Utm-Medium'] = utm.utm_medium;
  if (utm.utm_campaign) headers['X-Sellograph-Utm-Campaign'] = utm.utm_campaign;
  if (utm.utm_term) headers['X-Sellograph-Utm-Term'] = utm.utm_term;
  if (utm.utm_content) headers['X-Sellograph-Utm-Content'] = utm.utm_content;
  if (utm.click_id) headers['X-Sellograph-Click-Id'] = utm.click_id;
  if (utm.click_id_type) headers['X-Sellograph-Click-Id-Type'] = utm.click_id_type;
  if (utm.landing_url) headers['X-Sellograph-Landing-Url'] = utm.landing_url;
  if (utm.referrer) headers['X-Sellograph-Referrer'] = utm.referrer;
  // Identitaet — verknuepft Server-Events (Store-API) mit der Browser-Session,
  // ohne die die Attribution auf plugin_unresolved/direct zurueckfaellt.
  if (identity.visitor_id) headers['X-Sellograph-Visitor-Id'] = identity.visitor_id;
  if (identity.session_id) headers['X-Sellograph-Session-Id'] = identity.session_id;

  return headers;
}

// ─── sf.js Loader ───────────────────────────────────────────────────

/**
 * Laedt sf.js idempotent von der Worker-URL und initialisiert es mit dem
 * Token. Nutzt das offizielle Async-Loader-Muster: sfq()-Stub + Pre-Queue,
 * sf.js konsumiert window.sfq.q beim Laden (zusaetzlich data-token als
 * Fallback-Initialisierung).
 */
function loadSfJs(workerUrl: string, token: string): void {
  if (typeof document === 'undefined') return;
  if (document.querySelector('script[data-sellograph-loader]')) {
    dbg('sf.js bereits geladen/angefordert');
    return;
  }

  const w = window as any;
  w.sfq = w.sfq || function () {
    (w.sfq.q = w.sfq.q || []).push(arguments);
  };
  w.sfq('init', token);

  const s = document.createElement('script');
  s.src = workerUrl.replace(/\/+$/, '') + '/sf.js';
  s.async = true;
  s.setAttribute('data-sellograph-loader', '1');
  s.setAttribute('data-token', token);
  document.head.appendChild(s);
  dbg('sf.js Loader injiziert', s.src);
}

// ─── Nuxt Plugin ────────────────────────────────────────────────────

export default defineNuxtPlugin((nuxtApp) => {
  const config = useRuntimeConfig();

  // Support backendApiUrl (new), shopApiUrl, shopwareApiUrl (legacy)
  const backendApiUrl = (
    (config.public.backendApiUrl as string) ||
    (config.public.shopApiUrl as string) ||
    (config.public.shopwareApiUrl as string) ||
    ''
  ).replace(/\/+$/, '');

  const debugEnabled = config.public.sellographDebug === true || config.public.sellographDebug === 'true';

  // Activate debug mode
  if (debugEnabled) {
    setSellographDebug(true);
  }

  // Also allow runtime activation via console: window.__sellographDebug(true)
  if (typeof window !== 'undefined') {
    (window as any).__sellographDebug = setSellographDebug;
  }

  // Ensure sfq is available for the composable + load sf.js (self-contained —
  // no nuxt.config.ts head script needed).
  if (typeof window !== 'undefined') {
    (window as any).sfq = (window as any).sfq || function() {
      ((window as any).sfq.q = (window as any).sfq.q || []).push(arguments);
    };

    // Activate sf.js debug mode too (before sf.js loads)
    if (debugEnabled) {
      (window as any).__sg_debug = true;
    }

    const token = (config.public.sellographToken as string) || '';
    const workerUrl = ((config.public.sellographWorkerUrl as string) || '').replace(/\/+$/, '');
    if (token && workerUrl) {
      loadSfJs(workerUrl, token);
    } else {
      dbg('sf.js NICHT geladen — sellographToken/sellographWorkerUrl fehlen in runtimeConfig.public');
    }
  }

  dbg('Plugin initialisiert', { backendApiUrl: backendApiUrl || '(alle URLs)', debug: debugEnabled });

  // Capture UTMs on initial load + every navigation
  captureUtmParams();
  nuxtApp.hook('page:finish', () => {
    dbg('Navigation erkannt', window.location.pathname);
    captureUtmParams();
  });

  // Auto-inject UTM headers into backend API requests.
  //
  // CORS-SICHERHEIT: Custom Header an eine ANDERE Origin loesen einen
  // CORS-Preflight aus. Shopwares Store-API erlaubt X-Sellograph-* Header
  // erst ab Backend-Plugin 1.2 — sendet man sie vorher cross-origin, blockt
  // der Browser den GESAMTEN Request (Warenkorb/Checkout kaputt!). Darum:
  // same-origin wird immer injiziert; cross-origin NUR mit explizitem Opt-in
  // `sellographCrossOriginHeaders: true` (erst setzen, wenn das Backend-Plugin
  // >= 1.2 installiert ist).
  const crossOriginOptIn = config.public.sellographCrossOriginHeaders === true
    || config.public.sellographCrossOriginHeaders === 'true';

  const isSameOrigin = (urlStr: string): boolean => {
    if (typeof window === 'undefined') return false;
    try {
      return new URL(urlStr, window.location.origin).origin === window.location.origin;
    } catch {
      return false;
    }
  };

  // Statische Assets nie anfassen: das Backend-Plugin erweitert die CORS
  // Allow-List nur fuer /store-api — Header an z. B. /media/* wuerden den
  // Preflight scheitern lassen und den Asset-Request blocken.
  const ASSET_PATH_RE = /\/(media|thumbnail|theme|bundles|sitemap|_ipx|_nuxt)\//;
  const ASSET_EXT_RE = /\.(png|jpe?g|gif|webp|avif|svg|ico|css|js|mjs|map|woff2?|ttf|eot|mp4|webm|pdf|xml|txt)(\?|#|$)/i;

  const shouldInjectInto = (urlStr: string): boolean => {
    if (typeof window === 'undefined' || !urlStr) return false;
    if (ASSET_PATH_RE.test(urlStr) || ASSET_EXT_RE.test(urlStr)) return false;
    if (isSameOrigin(urlStr)) {
      return !backendApiUrl || urlStr.includes(backendApiUrl);
    }
    // Cross-origin: nur mit Opt-in UND explizit konfigurierter Backend-URL —
    // sonst wuerden Third-Party-Requests (Klaviyo, Hotjar, …) Custom-Header
    // bekommen, deren Preflight dort zwangslaeufig scheitert.
    // Zusaetzlich NUR /store-api: das Backend-Plugin erweitert die CORS
    // Allow-List ausschliesslich dort — die Admin-API (/api/oauth/token etc.)
    // kennt die Header nicht, ihr Preflight wuerde scheitern und den Request
    // blocken (so geschehen: Warenkorb kaputt via CheckoutSideCart-Admin-Login).
    return crossOriginOptIn
      && !!backendApiUrl
      && urlStr.includes(backendApiUrl)
      && urlStr.includes('/store-api/');
  };

  // 1) $fetch-Wrap — deckt Nuxt-eigene Requests und Custom-Code ab.
  const originalFetch = globalThis.$fetch;
  if (originalFetch) {
    globalThis.$fetch = Object.assign(
      ((url: any, opts: any = {}) => {
        if (typeof window !== 'undefined') {
          const urlStr = typeof url === 'string' ? url : url?.toString?.() || '';

          if (shouldInjectInto(urlStr)) {
            const sellographHeaders = getSellographHeaders();
            if (Object.keys(sellographHeaders).length > 0) {
              opts = {
                ...opts,
                headers: { ...(opts.headers || {}), ...sellographHeaders },
              };
              dbg('Headers injiziert →', urlStr, sellographHeaders);
            }
          } else if (urlStr && backendApiUrl && urlStr.includes(backendApiUrl) && !isSameOrigin(urlStr) && !crossOriginOptIn) {
            dbg('Headers NICHT injiziert (cross-origin ohne Opt-in) →', urlStr);
          }
        }
        return originalFetch(url, opts);
      }) as typeof originalFetch,
      originalFetch,
    );
  }

  // 2) window.fetch-Patch — PFLICHT fuer @shopware/api-client & Co.:
  // deren ofetch-Instanz (`ofetch.create()`) laeuft NIE durch globalThis.$fetch,
  // sondern ruft globalThis.fetch zur Callzeit auf. Ohne diesen Patch erreichen
  // die X-Sellograph-* Header die Store-API nie und jede Bestellung landet als
  // "direct" in der Attribution (so geschehen bei Luftkuss, 2026-07).
  if (typeof window !== 'undefined' && typeof window.fetch === 'function' && !(window.fetch as any).__sellographPatched) {
    const nativeFetch = window.fetch.bind(window);
    const patchedFetch = (input: RequestInfo | URL, init?: RequestInit) => {
      try {
        const urlStr = typeof input === 'string'
          ? input
          : input instanceof URL
            ? input.href
            : (input as Request)?.url || '';

        if (shouldInjectInto(urlStr)) {
          const sellographHeaders = getSellographHeaders();
          if (Object.keys(sellographHeaders).length > 0) {
            // init.headers ueberschreibt laut Fetch-Spec die Header eines
            // Request-Objekts komplett — darum beide Quellen mergen.
            const merged = new Headers(
              init?.headers ?? (typeof Request !== 'undefined' && input instanceof Request ? input.headers : undefined),
            );
            for (const [key, value] of Object.entries(sellographHeaders)) {
              if (!merged.has(key)) merged.set(key, value);
            }
            init = { ...(init || {}), headers: merged };
            dbg('Headers injiziert (fetch) →', urlStr, sellographHeaders);
          }
        }
      } catch {
        // Header-Merge darf niemals einen Request scheitern lassen
      }
      return nativeFetch(input as any, init);
    };
    (patchedFetch as any).__sellographPatched = true;
    window.fetch = patchedFetch as typeof window.fetch;
  }
});
